'use client';

import React, { useState } from 'react';
import { RefreshCw, ChevronDown, Calendar, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const properties = [
  { id: 'prop-001', domain: 'sc-domain:acme.io', label: 'acme.io' },
  { id: 'prop-002', domain: 'https://blog.acme.io/', label: 'blog.acme.io' },
  { id: 'prop-003', domain: 'https://docs.acme.io/', label: 'docs.acme.io' },
];

const dateRanges = [
  { id: 'range-7d', label: 'Last 7 days' },
  { id: 'range-28d', label: 'Last 28 days' },
  { id: 'range-90d', label: 'Last 90 days' },
  { id: 'range-6m', label: 'Last 6 months' },
];

export default function DashboardHeader() {
  const [selectedProperty, setSelectedProperty] = useState(properties?.[0]);
  const [selectedRange, setSelectedRange] = useState(dateRanges?.[2]);
  const [propertyOpen, setPropertyOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      toast?.success('GSC data synced successfully', {
        description: `${selectedProperty?.label} — data current as of Apr 6, 2026`,
      });
    }, 1800);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      {/* Left: Title + last sync */}
      <div>
        <h1 className="text-2xl font-semibold text-[#39364E] tracking-tight">
          Branded Traffic Dashboard
        </h1>
        <div className="flex items-center gap-2 mt-1">
          <CheckCircle2 size={13} className="text-emerald-500" />
          <span className="text-xs text-[#696969]">
            Last synced{' '}
            <span className="font-medium text-[#39364E]">Apr 6, 2026 at 11:32 AM</span>
          </span>
          <span className="text-[#e7e7e7]">·</span>
          <span className="text-xs text-[#696969]">Auto-syncs every 6h</span>
        </div>
      </div>

      {/* Right: Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Property Selector */}
        <div className="relative">
          <button
            onClick={() => { setPropertyOpen(!propertyOpen); setRangeOpen(false); }}
            className="flex items-center gap-2 h-9 px-3 bg-white border border-[#e7e7e7] rounded-[5px] text-sm font-medium text-[#39364E] hover:border-[rgba(150,25,50,0.4)] hover:bg-[rgba(150,25,50,0.02)] transition-all duration-150"
          >
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-mono text-xs">{selectedProperty?.label}</span>
            <ChevronDown
              size={14}
              className={`text-[#696969] transition-transform duration-150 ${
                propertyOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {propertyOpen && (
            <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.1)_0px_8px_24px] z-20 py-1 animate-fade-in">
              {properties?.map((p) => (
                <button
                  key={p?.id}
                  onClick={() => { setSelectedProperty(p); setPropertyOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[rgba(150,25,50,0.05)] transition-colors duration-100 ${
                    p?.id === selectedProperty?.id
                      ? 'text-[#961932] font-medium'
                      : 'text-[#39364E]'
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      p?.id === selectedProperty?.id ? 'bg-[#961932]' : 'bg-[#e7e7e7]'
                    }`}
                  />
                  <span className="font-mono text-xs">{p?.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date Range */}
        <div className="relative">
          <button
            onClick={() => { setRangeOpen(!rangeOpen); setPropertyOpen(false); }}
            className="flex items-center gap-2 h-9 px-3 bg-white border border-[#e7e7e7] rounded-[5px] text-sm font-medium text-[#39364E] hover:border-[rgba(150,25,50,0.4)] hover:bg-[rgba(150,25,50,0.02)] transition-all duration-150"
          >
            <Calendar size={14} className="text-[#696969]" />
            <span>{selectedRange?.label}</span>
            <ChevronDown
              size={14}
              className={`text-[#696969] transition-transform duration-150 ${
                rangeOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {rangeOpen && (
            <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-[#e7e7e7] rounded-[5px] shadow-[rgba(150,25,50,0.1)_0px_8px_24px] z-20 py-1 animate-fade-in">
              {dateRanges?.map((r) => (
                <button
                  key={r?.id}
                  onClick={() => { setSelectedRange(r); setRangeOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-[rgba(150,25,50,0.05)] transition-colors duration-100 ${
                    r?.id === selectedRange?.id
                      ? 'text-[#961932] font-medium'
                      : 'text-[#39364E]'
                  }`}
                >
                  {r?.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sync Button — Smarty CTA style */}
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 h-9 px-5 bg-[#961932] text-white rounded-[5px] text-sm font-medium hover:bg-[#8C0B31] active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed btn-smarty-shadow"
        >
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
          <span>{syncing ? 'Syncing…' : 'Sync Now'}</span>
        </button>
      </div>
    </div>
  );
}